import React, { useEffect, useState } from "react";
import { Avatar, Typography } from "@mui/material";
import Post from "./Post";
import { useNavigate } from "react-router-dom";
import addPost from "../assets/img/addPost2.svg";
import { CREATE_POST, GET_GROUP_POSTS } from "@constants/api";
import axios from "@MyAxios";
import { useDispatch, useSelector } from "react-redux";
import {
  addToGroups,
  addToPostCommunities,
  resetPost,
  setIsGroupOnlyPost,
} from "@redux/postSlice";
import ShowMore from "@components/ShowMore";
import { removeCommunityGroupPost } from "@redux/communitySlice";
import { toast } from "react-toastify";
import { setPage, setPosts, setTotalPages } from "@redux/groupSlice";
import { setClickedPostId } from "@redux/prevPostsSlice";
import Loader from "@components/Loader";
import { CircularProgress } from "@mui/material";

function GroupStreams({
  group,
  showOnlyEvents = false,
  postType = "",
  selectedTags = [],
  fromDate = null,
  toDate = null,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const groupId = group?.id;

  const { userRecord } = useSelector((state) => state.user);
  const { clickedPostId } = useSelector((state) => state.prevPosts);

  const { page, totalPages, posts } = useSelector((state) => state.group);
  const pageRef = React.useRef(page);
  const postsRef = React.useRef(posts);
  const totalPagesRef = React.useRef(totalPages);

  const post = useSelector((state) => state.post);

  // Track loading state so we never flash "No streams yet..." before fetch completes
  const [loading, setLoading] = useState(false);
  // Track whether the very first fetch for this groupId has finished
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [voteStatuses, setVoteStatuses] = useState({});

  useEffect(() => {
    if (!posts || posts.length === 0) return;

    const ids = posts
      .map((p) => {
        const isEvent =
          p.startTime ||
          p.endTime ||
          p.eventType ||
          String(p.id).startsWith("event-");

        return isEvent
          ? (String(p.id).startsWith("event-") ? p.id : `event-${p.id}`)
          : p.id;
      })
      .filter(Boolean);

    if (ids.length === 0) return;


    axios
      .post("/post/vote-status-bulk", {
        ids,
      })
      .then((res) => {
        setVoteStatuses((prev) => ({
          ...prev,
          ...res.data,
        }));
      })
      .catch((err) => {
        console.error("Failed to fetch bulk vote status", err);
      });

  }, [posts]);

  const getPosts = async () => {
    try {
      if (!groupId) return;
      setLoading(true);

      await axios
        .get(`${GET_GROUP_POSTS}/${groupId}`, {
          params: {
            page,
            limit: 15,
            postType,
            selectedTags,
            fromDate,
            toDate,
          },
        })
        .then((res) => {
          if (page === 1) dispatch(setPosts(res.data.posts));
          else dispatch(setPosts([...posts, ...res.data.posts]));
          dispatch(setTotalPages(res.data.totalPages));
        });
    } catch (error) {
    } finally {
      setLoading(false);
      setInitialLoaded(true);
    }
  };

  const refetchPosts = () => {
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      let postElement;
      if (clickedPostId) {
        postElement = document.getElementById(clickedPostId);
      }
      if (postElement) {
        clearInterval(interval);
        postElement.scrollIntoView({ behavior: "smooth" });
        dispatch(setClickedPostId(""));
      }
    }, 100);
  }, [clickedPostId]);

  const onCreateGroupPost = () => {
    if (group?.disabledAt > 0)
      return toast.error(
        "Action cannot be performed because group is disabled",
      );

    if (group?.community?.disabledAt > 0)
      return toast.error(
        "Action cannot be performed because group community is disabled",
      );

    dispatch(resetPost());
    dispatch(setIsGroupOnlyPost(true));
    dispatch(addToPostCommunities(group?.community?.id));
    dispatch(addToGroups(groupId));
    navigate(CREATE_POST);
  };

  const handleRemoveCommunityGroupPost = (postId) => {
    if (
      window.confirm("Are you sure you want to remove this post from group?")
    ) {
      dispatch(
        removeCommunityGroupPost({
          data: {
            postId,
            groupId,
            communityId: group?.community?.id,
          },
          onSuccess: refetchPosts,
        }),
      );
    }
  };

  useEffect(() => {
    // Reset initialLoaded when groupId changes so loader shows again for new group
    setInitialLoaded(false);

    const noFiltersApplied =
      (!selectedTags || selectedTags.length === 0) &&
      !fromDate &&
      !toDate &&
      !postType;

    if (noFiltersApplied && localStorage[`group-${groupId}-cache`]) {
      let groupCache = JSON.parse(localStorage[`group-${groupId}-cache`]);
      dispatch(setPage(groupCache.page));
      dispatch(setPosts(groupCache.posts));
      dispatch(setTotalPages(groupCache.totalPages));
      delete localStorage[`group-${groupId}-cache`];
      setInitialLoaded(true);
    } else {
      getPosts();
    }
  }, [groupId, page, selectedTags, fromDate, toDate, postType]);

  useEffect(() => {
    pageRef.current = page;
    postsRef.current = posts;
    totalPagesRef.current = totalPages;
  }, [page, posts, totalPages]);

  const cachePosts = () => {
    localStorage.setItem(
      `group-${groupId}-cache`,
      JSON.stringify({
        page: pageRef.current,
        posts: postsRef.current,
        totalPages: totalPagesRef.current,
      }),
    );
  };

  useEffect(() => {
    return () => {
      if (localStorage.postClickTrigger) cachePosts();
      dispatch(setPosts([]));
      dispatch(setPage(1));
      dispatch(setTotalPages(1));
    };
  }, []);

  const userCanRemovePostsAsCommunityAdmin =
    group?.community?.adminAccessLevels?.canDeleteCommunityPost &&
    group?.community?.admin === userRecord?.id;
  const communityOrGroupDisabled =
    group?.disabledAt > 0 || group?.community?.disabledAt > 0;
  const groupAdminCanDeletePost = group?.admin?.id === userRecord?.id;

  const isEventPost = (post) => {
    if (!post) return false;
    if (
      post.startTime ||
      post.endTime ||
      post.eventType ||
      post.attendeesFrom ||
      post.admissionFeeType
    )
      return true;
    if (post.start_time || post.end_time || post.event_type) return true;
    if (
      post.type === "event" ||
      post.postType === "event" ||
      post.kind === "event" ||
      post.isEvent === true
    )
      return true;
    try {
      const keys = Object.keys(post || {});
      if (
        keys.some((k) => {
          const lk = String(k).toLowerCase();
          return (
            lk.includes("event") ||
            lk.includes("start") ||
            lk.includes("end") ||
            lk.includes("attend") ||
            lk.includes("admiss") ||
            lk.includes("fee")
          );
        })
      )
        return true;
    } catch (e) { }
    return false;
  };

  let displayedPosts = posts;
  if (postType === "event")
    displayedPosts = displayedPosts?.filter(isEventPost);
  else if (postType === "post")
    displayedPosts = displayedPosts?.filter((p) => !isEventPost(p));
  if (showOnlyEvents) displayedPosts = displayedPosts?.filter(isEventPost);

  // Show centred spinner while first fetch is in flight
  if (loading && !initialLoaded) {
    return (
      <div className="flex justify-center items-center py-12">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      {/* Only show empty message once we know the fetch has returned nothing */}
      {initialLoaded && displayedPosts?.length === 0 && (
        <Typography className="mt-4">
          {showOnlyEvents ? "No events yet..." : "No streams yet..."}
        </Typography>
      )}

      {displayedPosts?.map((item) => (
        <div key={item.id} id={`post-${item.id}`}>
          <Post
            voteStatus={
              voteStatuses[
              item?.startTime || item?.eventType || String(item.id).startsWith("event-")
                ? `event-${item.id.replace("event-", "")}`
                : item.id
              ]
            }
            profilePost={true}
            comment
            group={
              group ? { ...group, name: undefined, icon: undefined } : group
            }
            post={item}
            canDeletePost={groupAdminCanDeletePost}
            removePost={refetchPosts}
            readOnly={communityOrGroupDisabled}
            canRemoveCommunityGroupPost={userCanRemovePostsAsCommunityAdmin}
            handleRemoveCommunityGroupPost={handleRemoveCommunityGroupPost}
          />
        </div>
      ))}

      {/* Inline spinner at bottom when loading more pages */}
      {loading && initialLoaded && (
        <div className="flex justify-center items-center py-4">
          <CircularProgress size="1.5rem" />
        </div>
      )}

      {!loading && page < totalPages && (
        <div
          onClick={() => dispatch(setPage(page + 1))}
          className="cursor-pointer my-2 text-center mx-4"
        >
          <ShowMore>Show More</ShowMore>
        </div>
      )}

      {(group?.members?.map((x) => x.id).includes(userRecord.id) ||
        group?.admin?.id === userRecord.id) && (
          <div
            onClick={onCreateGroupPost}
            className="fixed bottom-10 right-5 md:right-1/4 cursor-pointer"
          >
            <Avatar className="h-12 w-12 md:h-20 md:w-20" src={addPost} />
          </div>
        )}
    </div>
  );
}

export default GroupStreams;
